/**
 * HTTP request layer
 * if auth is required return patched axios instance(with access token in headers)
 * else return clear axios instance
 */

import axios from 'axios';
import getEnv from '@/utils/env';
import { AuthService } from './auth.service';
import router from '@/router';
import $store from '@/store/index';
import errorCodeMessage from '@/utils/custom/errorCodes.js';
import i18n from '@/plugins/i18n';
import { EventTypes } from '@/utils/enums/eventTypes';
import timeFormat from '@/utils/time/timeFormat';
import { notificationError } from '@/utils/notification';

function findKeys(obj, keyExistence) {
  if (typeof obj !== 'object' || obj === null) {
    return; // Base case: not an object or null
  }

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      // Check if the current key is in the serviceKeys array
      if (serviceKeys.includes(key)) {
        keyExistence[key] = obj[key]; // Mark the key as existing
      }

      if (typeof obj[key] === 'object' && obj[key] !== null) {
        findKeys(obj[key], keyExistence); // Recursive call for nested objects
      }
    }
  }
}

function checkSpecificKeys(jsonData, serviceKeys) {
  try {
    // Parse the JSON string if it's not already an object
    if (typeof jsonData === 'string') {
      jsonData = JSON.parse(jsonData);
    }

    const keyExistence = {}; // Object to store whether each key exists

    findKeys(jsonData, keyExistence); // Start the recursion

    return keyExistence;
  } catch (error) {
    console.error('Error parsing JSON or accessing properties:', error);
    // Return an object with all keys set to false in case of an error
    const errorResult = {};
    serviceKeys.forEach((key) => {
      errorResult[key] = false;
    });
    return errorResult;
  }
}

const serviceKeys = ['serviceId', 'serviceName', 'taskID'];

const BASE_URL =
  getEnv('VUE_APP_AUTH_HOST') ||
  process.env.VUE_APP_AUTH_HOST ||
  'http://localhost:4000';

// TODO Finish the integration with error function below
async function handleCustomEvent(event) {
  await $store.dispatch('globalState/user/getProfile').then((resp) => {});
  setTimeout(() => {
    const gap =
      $store.getters['globalState/settings/durationShow'].end.getTime() -
      $store.getters['globalState/settings/durationShow'].start.getTime();
    const time = [new Date(new Date().getTime() - gap), new Date()];
    $store.dispatch('globalState/settings/setDuration', timeFormat(time));
  }, 2000);
}

function handleCustomCalendar(event) {
  $store.commit('deState/setCurrentDeTab', event.detail.pathname);
}

// Add an event listener for the custom event
document.addEventListener(EventTypes.SS_REQUEST_FAILED_401, handleCustomEvent);
document.addEventListener(EventTypes.SS_DISABLE_CALENDAR, handleCustomCalendar);

let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (cb) => {
  refreshSubscribers.push(cb);
};

const onRefreshed = () => {
  refreshSubscribers.map((cb) => cb());
};

export default class Http {
  constructor(status) {
    this.isAuth = status && status.auth ? status.auth : false;
    this.instance = axios.create({
      baseURL: BASE_URL,
      withCredentials: true,
    });
    this.instance.defaults.withCredentials = true;
    this.instance.defaults.headers.common = {
      Accept: 'application/json,application/hal+json',
      'Content-Type': 'application/json',
    };
    return this.init();
  }

  retryRequest(originalRequest) {
    this.instance(originalRequest);
  }
  init() {
    //Request Interceptor
    this.instance.interceptors.request.use(
      function (config) {
        // Do something before request is sent
        if (
          config?.data?.variables &&
          !config.data?.query.includes('getGraphDataPulse')
        ) {
          const keyMap = checkSpecificKeys(
            config?.data?.variables || {},
            serviceKeys,
          );

          const allKeys = Object.keys(keyMap);
          for (const key of allKeys) {
            const isArray = Array.isArray(keyMap[key]);
            if (!keyMap[key] || (isArray && keyMap[key].length === 0)) {
              return Promise.reject(
                new Error('request blocked without service id'),
              ); // Stop the request
            }
          }
        }

        return config;
      },
      function (error) {
        // Do something with request error
        return Promise.reject(error);
      },
    );
    // Add a response interceptor
    this.instance.interceptors.response.use(
      function (response) {
        // Any status code that lie within the range of 2xx cause this function to trigger
        // Do something with response data
        /***
         * Temporary fix, needs to fixed after backend implementation*
         */
        var code = response?.data?.errorCode;
        if (errorCodeMessage.has(code)) {
          var message = errorCodeMessage.get(code);
          console.log('error', response.data);
          notificationError((response.data.message = message));
          return Promise.reject(response);
        }
        return response;
      },
      // on error
      async (error) => {
        // Any status codes that falls outside the range of 2xx cause this function to trigger
        // Do something with response error
        var code = error?.response?.data?.errorCode;
        const originalRequest = error.config;
        const axiosInstance = this.instance;
        if (
          error?.response?.status === 401 &&
          originalRequest.url.includes('tokens/access')
        ) {
          //log out user if access token returns 401
          AuthService.makeLogout();
          return Promise.reject(error);
        } else if (error?.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          if (isRefreshing) {
            // Queue the request if refresh is in progress
            return new Promise(function (resolve, reject) {
              subscribeTokenRefresh(() => {
                resolve(axiosInstance(originalRequest)); // Use this.instance to maintain context
              });
            })
              .then((response) => response)
              .catch((err) => {
                return Promise.reject(err);
              });
          }

          isRefreshing = true;
          return new Promise(function (resolve, reject) {
            AuthService.refreshTokens(
              $store.getters['globalState/user/getTeamId'],
            )
              .then(() => {
                onRefreshed();
                resolve(axiosInstance(originalRequest)); // Use this.instance to maintain context
              })
              .catch((err) => {
                reject(err);
              })
              .finally(() => {
                isRefreshing = false;
                refreshSubscribers = []; // Clear subscribers after refresh
              });
          });
        }
        // Handle 403 Forbidden status
        else if (error?.response?.status === 403) {
          console.log('403 Forbidden: Redirecting to permissionDenied route');
          notificationError(i18n.global.t('permission-denied')); //TODO: needs to update the notification message
          // Redirect user to the permissionDenied route
          router.push({
            name: 'permissionDenied',
            query: { updateTeamList: 'true' },
          });
          return Promise.reject(error);
        }
        //Custom Dashboard Error Handling
        else if (
          error?.response?.status === 404 &&
          errorCodeMessage.has(code)
        ) {
          var message = errorCodeMessage.get(code);
          console.log('error', error.response);
          notificationError((error.message = message));
          return Promise.reject(error);
        }
        // 401, 200, 201, 204
        else if (
          error?.response?.status !== 401 &&
          error?.response?.status !== 200 &&
          error?.response?.status !== 201
        ) {
          // Disaabling error notification in order to handle from service layer
          // Vue.prototype.$notification.error(
          //   (error.message = i18n.global.t('errorText')),
          //   {
          //     timer: 4,
          //     showLeftIcn: false,
          //     showCloseIcn: true
          //   }
          // );
          return Promise.reject(error);
        }
        return Promise.reject(error);
      },
    );
    return this.instance;
  }
}
